import { inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { isMdOrLargerDialogWidth } from '@shared/consts';

/** Extend this class to add programmatic fullscreen dialog functionality outside the header.
 * Prefer {@link CipoDialogHeaderComponent} for standard dialog title bars.
 *
 * Make sure to add the 'cipo--height--full' class to the dialog panelClass to ensure proper height handling.
 */
export abstract class FullScreenDialogBase<T = unknown> {
  dialogRef = inject(MatDialogRef<T>);

  isFullscreen = signal(false);
  canToggleFullscreen = signal(false);

  private initialWidth = '';
  private initialMinHeight = '';
  private initialMaxHeight = '';

  constructor() {
    const { width, minHeight, maxHeight } =
      (
        this.dialogRef as MatDialogRef<T> & {
          _containerInstance?: {
            _config?: { width?: string; minHeight?: string | number; maxHeight?: string | number };
          };
        }
      )._containerInstance?._config ?? {};

    this.initialWidth = width ?? '';
    this.initialMinHeight = typeof minHeight === 'number' ? `${minHeight}px` : (minHeight ?? '');
    this.initialMaxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : (maxHeight ?? '');
    this.canToggleFullscreen.set(isMdOrLargerDialogWidth(this.initialWidth));
  }

  toggleFullscreen() {
    if (!this.canToggleFullscreen()) {
      return;
    }

    const isFs = this.isFullscreen();
    this.isFullscreen.set(!isFs);

    const container = (
      this.dialogRef as MatDialogRef<T> & {
        _containerInstance?: { _elementRef?: { nativeElement: HTMLElement } };
      }
    )._containerInstance?._elementRef?.nativeElement;
    const overlay = container?.closest('.cdk-overlay-pane') as HTMLElement | null;

    if (!isFs) {
      this.dialogRef.updateSize('100vw', '100vh');

      if (container) {
        container.style.minHeight = '100vh';
        container.style.maxHeight = '100vh';
      }
      if (overlay) {
        overlay.style.maxHeight = '100vh';
      }
    } else {
      this.dialogRef.updateSize(this.initialWidth || undefined, 'auto');

      if (container) {
        container.style.height = 'auto';
        container.style.minHeight = this.initialMinHeight;
        container.style.maxHeight = this.initialMaxHeight;
      }
      if (overlay) {
        overlay.style.maxHeight = this.initialMaxHeight;
      }
    }
  }
}

/** Shared fullscreen logic for components that cannot extend FullScreenDialogBase (e.g. DirtyDialogBase). */
export class DialogFullscreenState {
  private initialWidth = '';
  private initialMinHeight = '';
  private initialMaxHeight = '';

  readonly isFullscreen = signal(false);
  readonly canToggleFullscreen = signal(false);

  constructor(private dialogRef: MatDialogRef<unknown>) {
    const { width, minHeight, maxHeight } =
      (
        this.dialogRef as MatDialogRef<unknown> & {
          _containerInstance?: {
            _config?: { width?: string; minHeight?: string | number; maxHeight?: string | number };
          };
        }
      )._containerInstance?._config ?? {};

    this.initialWidth = width ?? '';
    this.initialMinHeight = typeof minHeight === 'number' ? `${minHeight}px` : (minHeight ?? '');
    this.initialMaxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : (maxHeight ?? '');
    this.canToggleFullscreen.set(isMdOrLargerDialogWidth(this.initialWidth));
  }

  toggleFullscreen() {
    if (!this.canToggleFullscreen()) {
      return;
    }

    const isFs = this.isFullscreen();
    this.isFullscreen.set(!isFs);

    const container = (
      this.dialogRef as MatDialogRef<unknown> & {
        _containerInstance?: { _elementRef?: { nativeElement: HTMLElement } };
      }
    )._containerInstance?._elementRef?.nativeElement;
    const overlay = container?.closest('.cdk-overlay-pane') as HTMLElement | null;

    if (!isFs) {
      this.dialogRef.updateSize('100vw', '100vh');

      if (container) {
        container.style.minHeight = '100vh';
        container.style.maxHeight = '100vh';
      }
      if (overlay) {
        overlay.style.maxHeight = '100vh';
      }
    } else {
      this.dialogRef.updateSize(this.initialWidth || undefined, 'auto');

      if (container) {
        container.style.height = 'auto';
        container.style.minHeight = this.initialMinHeight;
        container.style.maxHeight = this.initialMaxHeight;
      }
      if (overlay) {
        overlay.style.maxHeight = this.initialMaxHeight;
      }
    }
  }
}
